import { useEffect, useState } from "react";
import {
  format,
  isBefore,
  startOfDay,
  endOfDay,
  startOfMonth,
  subDays,
  differenceInDays,
} from "date-fns";
import { ptBR, enUS } from "date-fns/locale";
import { CalendarIcon, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { DateRange } from "react-day-picker";
import { Button } from "@akuna/ui";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { CompareMode } from "@/lib/utils/comparison-dates";
import { getComparisonRange } from "@/lib/utils/comparison-dates";

interface RevenuePeriodHeroProps {
  dateFrom: Date;
  dateTo: Date;
  compareMode: CompareMode;
  netRevenue: number;
  netRevenuePrev: number;
  onDateFromChange: (d: Date) => void;
  onDateToChange: (d: Date) => void;
  onCompareModeChange: (m: CompareMode) => void;
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}


export function RevenuePeriodHero({
  dateFrom,
  dateTo,
  compareMode,
  netRevenue,
  netRevenuePrev,
  onDateFromChange,
  onDateToChange,
  onCompareModeChange,
}: RevenuePeriodHeroProps) {
  const { t, i18n } = useTranslation("reports");
  const locale = i18n.language === "pt" ? ptBR : enUS;
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [range, setRange] = useState<DateRange | undefined>({ from: dateFrom, to: dateTo });
  const [selectionStep, setSelectionStep] = useState<"from" | "to">("from");

  useEffect(() => {
    setRange({ from: dateFrom, to: dateTo });
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (calendarOpen) setSelectionStep("from");
  }, [calendarOpen]);

  const comparison =
    compareMode !== "none" ? getComparisonRange(dateFrom, dateTo, compareMode) : null;
  const periodLabel = `${format(dateFrom, "dd MMM yyyy", { locale })} – ${format(dateTo, "dd MMM yyyy", { locale })}`;
  const compLabel = comparison
    ? `${format(comparison.compareFrom, "dd MMM yyyy", { locale })} – ${format(comparison.compareTo, "dd MMM yyyy", { locale })}`
    : null;
  const dayCount = differenceInDays(dateTo, dateFrom) + 1;

  const delta = netRevenuePrev > 0 ? ((netRevenue - netRevenuePrev) / netRevenuePrev) * 100 : null;
  const deltaPositive = delta !== null && delta > 0;
  const deltaNegative = delta !== null && delta < 0;

  function applyQuick(type: "today" | "7d" | "30d" | "month") {
    const now = new Date();
    const to = endOfDay(now);
    const from =
      type === "today"
        ? startOfDay(now)
        : type === "7d"
        ? startOfDay(subDays(now, 6))
        : type === "30d"
        ? startOfDay(subDays(now, 29))
        : startOfMonth(now);
    onDateFromChange(from);
    onDateToChange(to);
    setRange({ from, to });
    setSelectionStep("from");
  }

  function isQuickActive(type: "today" | "7d" | "30d" | "month") {
    const now = new Date();
    if (type === "today")
      return (
        format(dateFrom, "yyyy-MM-dd") === format(startOfDay(now), "yyyy-MM-dd") &&
        format(dateTo, "yyyy-MM-dd") === format(endOfDay(now), "yyyy-MM-dd")
      );
    if (type === "7d")
      return (
        format(dateFrom, "yyyy-MM-dd") === format(startOfDay(subDays(now, 6)), "yyyy-MM-dd")
      );
    if (type === "30d")
      return (
        format(dateFrom, "yyyy-MM-dd") === format(startOfDay(subDays(now, 29)), "yyyy-MM-dd")
      );
    return format(dateFrom, "yyyy-MM-dd") === format(startOfMonth(now), "yyyy-MM-dd");
  }

  function handleDayClick(day: Date) {
    const clicked = startOfDay(day);
    if (selectionStep === "from") {
      setRange({ from: clicked, to: undefined });
      onDateFromChange(clicked);
      onDateToChange(endOfDay(clicked));
      setSelectionStep("to");
      return;
    }
    const from = range?.from ? startOfDay(range.from) : clicked;
    const nextFrom = isBefore(clicked, from) ? clicked : from;
    const nextTo = isBefore(clicked, from) ? from : clicked;
    setRange({ from: nextFrom, to: nextTo });
    onDateFromChange(nextFrom);
    onDateToChange(endOfDay(nextTo));
    setSelectionStep("from");
    setCalendarOpen(false);
  }

  const quickLabels = {
    today: t("quickToday"),
    "7d": t("quick7d"),
    "30d": t("quick30d"),
    month: t("quickThisMonth"),
  };

  return (
    <div className="rounded-2xl border bg-card p-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
      {/* Left — context */}
      <div className="flex flex-col gap-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-primary shrink-0" />
          <span className="text-xs font-semibold uppercase tracking-wider text-primary">
            {t("revenueOperation")}
          </span>
        </div>

        <div className="flex items-baseline gap-3 flex-wrap mt-1">
          <span className="text-3xl font-extrabold tracking-tight">
            {formatCurrency(netRevenue)}
          </span>
          {netRevenuePrev > 0 && (
            <span className="text-base font-semibold text-muted-foreground">
              {formatCurrency(netRevenuePrev)}
            </span>
          )}
          {delta !== null && (
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                deltaPositive
                  ? "bg-green-100 text-green-800"
                  : deltaNegative
                  ? "bg-red-100 text-red-800"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {deltaPositive ? (
                <TrendingUp className="h-3 w-3" />
              ) : deltaNegative ? (
                <TrendingDown className="h-3 w-3" />
              ) : (
                <Minus className="h-3 w-3" />
              )}
              {delta > 0 ? "+" : ""}
              {delta.toFixed(1)}%
            </span>
          )}
        </div>

        <p className="text-sm text-muted-foreground mt-1">
          {t("revenueHeroPeriod", { days: dayCount, period: periodLabel })}
        </p>

        {compLabel && (
          <p className="text-xs text-muted-foreground">
            {t("comparedTo")} <span>{compLabel}</span>
          </p>
        )}

      </div>

      {/* Right — controls */}
      <div className="flex flex-col gap-3 shrink-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          {(["today", "7d", "30d", "month"] as const).map((q) => (
            <Button
              key={q}
              size="sm"
              variant={isQuickActive(q) ? "default" : "outline"}
              className="h-7 px-3 text-xs rounded-full"
              onClick={() => applyQuick(q)}
            >
              {quickLabels[q]}
            </Button>
          ))}
        </div>

        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 text-xs gap-2 justify-start">
              <CalendarIcon className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate max-w-[220px]">{periodLabel}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="range"
              selected={range}
              onDayClick={handleDayClick}
              numberOfMonths={2}
              locale={locale}
            />
          </PopoverContent>
        </Popover>

        <Select
          value={compareMode}
          onValueChange={(v) => onCompareModeChange(v as CompareMode)}
        >
          <SelectTrigger className="h-8 text-xs w-full">
            <SelectValue placeholder={t("compareModeLabel")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="prev-month">{t("compareModePrevMonth")}</SelectItem>
            <SelectItem value="prev-year">{t("compareModePrevYear")}</SelectItem>
            <SelectItem value="prev-period">{t("compareModePrevPeriod")}</SelectItem>
            <SelectItem value="none">{t("compareModeNone")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
